import { Experiment, Layer, useStatsigClient } from "@statsig/react-bindings";
import Cookies from "js-cookie";
import { useEffect, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";

import { ABTestingId } from "@every.org/common/src/codecs/entities";
import { CookieKey } from "@every.org/common/src/entity/types/cookies";

import {
  AbTestActionType,
  dispatchAbTestAction,
} from "src/context/AbTestContext";
import { AuthState } from "src/context/AuthContext/types";
import { trackABTest } from "src/utility/analytics";
import { parseCookieFromString, setCookie } from "src/utility/cookies";
import { getClientBotType } from "src/utility/helpers";
import { logger } from "src/utility/logger";

/**
 * Days that a logged out user's testing ID cookie is kept alive; upon expiry,
 * if the user made an account, future tests will use their user ID instead
 */
export const TEST_ID_CACHE_EXPIRY_DAYS = 365;

const skipExperiments = process.env.NEXT_PUBLIC_SKIP_EXPERIMENTS === "true";

export function getTestingIdFromCookie(): ABTestingId {
  return Cookies.get(CookieKey.TESTING_ID_CACHE) as ABTestingId;
}

export function createTestingId(): ABTestingId {
  // Generates a new uuid to use as the testing id
  return uuidV4() as ABTestingId;
}

export function resetTestingIdInCookie(id: ABTestingId) {
  setCookie(CookieKey.TESTING_ID_CACHE, id, {
    expires: TEST_ID_CACHE_EXPIRY_DAYS,
  });
}

function createTestingIdInCookie(): ABTestingId {
  const newId = createTestingId();
  setCookie(CookieKey.TESTING_ID_CACHE, newId, {
    expires: TEST_ID_CACHE_EXPIRY_DAYS,
  });
  return newId as ABTestingId;
}

export function getTestingIdFromServerCookie(cookie?: string): ABTestingId {
  const cookieObj = parseCookieFromString(cookie);
  return cookieObj[CookieKey.TESTING_ID_CACHE] as ABTestingId;
}

export function getStableIdFromServerCookie(cookie?: string): ABTestingId {
  const cookieObj = parseCookieFromString(cookie);
  return cookieObj[CookieKey.STABLE_ID] as ABTestingId;
}

/**
 * Gets the canonical id for the user to ensure they are assigned consistent
 * variants across a session for A/B tests.
 *
 * For any user with a session, the user's ID serves as a stable ID; but logged
 * out users don't have a user ID, so we create a random testing ID and store it
 * in a cookie instead (consider that this id might have been generated on the
 * server rendering side and might not be in a cookie yet).
 */
export function getTestingId({
  authState,
  createIdIfMissing = true,
  ssrTestingId,
}: {
  authState: AuthState | null;
  createIdIfMissing?: boolean;
  ssrTestingId?: string | undefined;
}): ABTestingId | undefined {
  const firstOption =
    authState?.user?.abTestingId ||
    authState?.guestUser?.abTestingId ||
    authState?.abTestingId ||
    getTestingIdFromCookie();
  if (firstOption) {
    return firstOption;
  }
  if (ssrTestingId) {
    setCookie(CookieKey.TESTING_ID_CACHE, ssrTestingId, {
      expires: TEST_ID_CACHE_EXPIRY_DAYS,
    });
    return ssrTestingId as ABTestingId;
  }
  if (createIdIfMissing) {
    return createTestingIdInCookie();
  }
  return undefined;
}

export function getStableId() {
  let stableId = Cookies.get(CookieKey.STABLE_ID);
  if (!stableId) {
    stableId = createTestingId();
    setCookie(CookieKey.STABLE_ID, stableId, {
      expires: TEST_ID_CACHE_EXPIRY_DAYS,
    });
  }
  return stableId;
}

/**
 * Uses a statsig layer or experiment.
 */
export function useStatSig(
  name: string,
  useFn: "layer" | "experiment",
  skipAbTest: boolean
) {
  const { client } = useStatsigClient();
  const isBot = !!getClientBotType();
  // Potentially not needed since exposure logs are not logged
  // until layer.getValue is called. So we could just not call that
  const exposureLoggingDisabled = skipAbTest || isBot || skipExperiments;

  const args = { disableExposureLog: exposureLoggingDisabled };
  const layerOrExperiment =
    client.loadingStatus === "Ready"
      ? useFn === "layer"
        ? client.getLayer(name, args)
        : client.getExperiment(name, args)
      : undefined;

  useEffect(() => {
    if (exposureLoggingDisabled || !layerOrExperiment) {
      return;
    }
    const layerAndGroup =
      layerOrExperiment.name + "-group-" + layerOrExperiment.groupName;
    trackABTest(name, layerAndGroup);
    // The group is usually "null" unless we have explicitly set it as a parameter.
    // We will have to do that for all groups where we want to log the exposure.
    layerOrExperiment.groupName &&
      dispatchAbTestAction({
        type: AbTestActionType.ADD_EXPOSURE,
        layer: layerAndGroup,
      });
  }, [exposureLoggingDisabled, name, layerOrExperiment]);

  const useFallbackLayer =
    exposureLoggingDisabled ||
    !layerOrExperiment ||
    typeof layerOrExperiment.get !== "function";

  return useMemo(
    () =>
      useFallbackLayer
        ? {
            get: (key: string, defaultValue: any) => {
              if (!exposureLoggingDisabled) {
                logger.warn({
                  message:
                    "Statsig layer not loaded, returning default value for key.",
                  data: { key, defaultValue, name },
                });
              }
              return defaultValue;
            },
          }
        : layerOrExperiment,
    [layerOrExperiment, useFallbackLayer, name, exposureLoggingDisabled]
  );
}

export function useUserLayer(layerName: string, skipAbTest: boolean) {
  return useStatSig(layerName, "layer", skipAbTest) as Layer;
}

export function useUserExperiment(
  experimentName: string,
  skipAbTest: boolean
): Experiment {
  return useStatSig(experimentName, "experiment", skipAbTest) as Experiment;
}

function useNonprofitLayerOrExperiment(
  layerName: string,
  useFn: typeof useUserLayer | typeof useUserExperiment,
  skipAbTest: boolean,
  nonprofitId?: string
) {
  // Sync vs Async functions similarly to client initialization
  // https://docs.statsig.com/client/javascript-sdk/migrating-from-statsig-js#updating-the-user
  // https://docs.statsig.com/client/javascript-sdk/init-strategies#3-asynchronous-initialization---not-awaited
  const { client } = useStatsigClient();

  const nonprofitIdIsSet =
    client.loadingStatus === "Ready" &&
    client.getContext().user.customIDs?.nonprofitId;

  const layer = useFn(
    layerName,
    !nonprofitIdIsSet || skipAbTest || !nonprofitId
  );

  return layer;
}

export function useNonprofitLayer(
  layerName: string,
  skipAbTest: boolean,
  nonprofitId?: string
) {
  return useNonprofitLayerOrExperiment(
    layerName,
    useUserLayer,
    skipAbTest,
    nonprofitId
  );
}

export function useNonprofitExperiment(
  experimentName: string,
  skipAbTest: boolean,
  nonprofitId?: string
) {
  return useNonprofitLayerOrExperiment(
    experimentName,
    useUserExperiment,
    skipAbTest,
    nonprofitId
  );
}
